import axios from "axios"; // רק עבור isAxiosError
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../services/axios";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { Category, FormDto } from "../types/category.types";
import styles from "./css/UploadFormPage.module.css";

interface FileUploadModalProps {
  onFormUploaded?: (newForm: FormDto) => void;
  isAnalyst?: boolean;
  analystId?: number;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  onFormUploaded,
  isAnalyst = false,
  analystId = 0,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get<Category[]>("/FormCategory");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setMessage("שגיאה בטעינת הקטגוריות");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setMessage("");
    }
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(parseInt(event.target.value));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("אנא בחר קובץ להעלאה");
      return;
    }

    if (!selectedCategory || selectedCategory === 0) {
      setMessage("אנא בחר קטגוריה");
      return;
    }

    setIsUploading(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("FormFile", selectedFile);
      formData.append("FormCategoryCode", selectedCategory.toString());
      if (isAnalyst && analystId) {
        formData.append("AnalystId", analystId.toString());
      } else if (user?.code) {
        formData.append("UserId", user.code.toString());
      }

      const response = await axiosInstance.post<FormDto>("/Form/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setMessage("הקובץ הועלה בהצלחה!");
      setSelectedFile(null);
      setSelectedCategory(0);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onFormUploaded && response.data) {
        onFormUploaded(response.data);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      if (axios.isAxiosError(error) && error.response) {
        setMessage(`שגיאה בהעלאת הקובץ: ${error.response.data?.message || error.message}`);
      } else {
        setMessage("שגיאה בהעלאת הקובץ");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setSelectedCategory(0);
    setMessage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div 
      className={styles.uploadFormContainer}
      style={{
        width: '100%',
        maxWidth: '500px',
        margin: '2rem auto',
        padding: '2.5rem',
        background: '#ffffff',
        borderRadius: '15px',
        direction: 'rtl',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.5rem',
        boxShadow: '0 10px 30px rgba(238, 192, 198, 0.3)',
        border: '2px solid #eec0c6'
      }}
    >
      <h2 
        className={styles.title}
        style={{
          fontSize: '2rem',
          color: '#2c3e50',
          marginBottom: '2rem',
          textAlign: 'center',
          fontWeight: '600',
          background: 'linear-gradient(135deg, #eec0c6 0%, #e0efbb 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        {isAnalyst ? "העלאת טופס חדש" : "העלאת טופס"}
      </h2>

      <div 
        className={styles.categorySection}
        style={{
          width: '100%',
          margin: '1.5rem 0',
          padding: '1.5rem',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '12px',
          boxShadow: '0 3px 10px rgba(238, 192, 198, 0.2)',
          border: '1px solid rgba(238, 192, 198, 0.3)'
        }}
      >
        <label 
          className={styles.categoryLabel} 
          htmlFor="categorySelect"
          style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '0.8rem',
            display: 'block',
            textAlign: 'center'
          }}
        >
          בחר קטגוריה:
        </label>
        <select
          id="categorySelect"
          value={selectedCategory}
          onChange={handleCategoryChange}
          className={styles.selectCategory}
          disabled={isUploading}
          style={{
            width: '100%',
            padding: '1rem 1.2rem',
            fontSize: '1rem',
            border: '2px solid rgba(238, 192, 198, 0.4)',
            borderRadius: '10px',
            backgroundColor: '#fff',
            color: '#2c3e50',
            cursor: 'pointer',
            appearance: 'none',
            direction: 'rtl'
          }}
        >
          <option value={0}>בחר קטגוריה...</option>
          {categories.map((category) => (
            <option key={category.code} value={category.code}>
              {category.categoryName}
            </option>
          ))}
        </select>
      </div>

      <div 
        className={styles.fileUploadSection}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2rem',
          marginBottom: '2rem',
          width: '100%'
        }}
      >
        <button
          onClick={() => fileInputRef.current?.click()}
          className={styles.uploadButton}
          disabled={isUploading}
          style={{
            background: 'linear-gradient(135deg, #eec0c6 0%, #f8ddcd 100%)',
            color: '#2c3e50',
            padding: '1.2rem 2rem',
            fontSize: '1.1rem',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            width: '200px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.8rem',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: '0 5px 15px rgba(238, 192, 198, 0.4)'
          }}
        >
          📁 בחר קובץ
        </button>
        <input
          ref={fileInputRef}
          id="fileInput"
          type="file"
          onChange={handleFileSelect}
          className={styles.fileInput}
          disabled={isUploading}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
        {selectedFile && (
          <div className={styles.filePreview}>
            <span className={styles.fileName}>{selectedFile.name}</span>
            <span className={styles.fileSize}>
              ({(selectedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
        )}
      </div>

      <div 
        className={styles.buttonGroup}
        style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          marginTop: '2rem'
        }}
      >
        <button
          onClick={handleUpload}
          disabled={isUploading || !selectedFile || !selectedCategory}
          className={styles.submitButton}
          style={{
            background: 'linear-gradient(135deg, #e0efbb 0%, #dcc8ec 100%)',
            color: '#2c3e50',
            padding: '1rem 2rem',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            minWidth: '120px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 5px 15px rgba(224, 239, 187, 0.4)'
          }}
        >
          {isUploading ? "מעלה..." : "שלח"}
        </button>

        <button
          onClick={handleCancel}
          disabled={isUploading}
          className={styles.cancelButton}
          style={{
            background: 'linear-gradient(135deg, #eec0c6 0%, #f8ddcd 100%)',
            color: '#2c3e50',
            padding: '1rem 2rem',
            fontSize: '1.1rem',
            fontWeight: '600',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            minWidth: '120px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: '0 5px 15px rgba(238, 192, 198, 0.4)'
          }}
        >
          ביטול
        </button>
      </div>

      {message && <p className={styles.message}>{message}</p>}
    </div>
  );
};

export default FileUploadModal;
